// eslint-disable-next-line
import { injectable } from "inversify";
import {
    ICreateCheckoutSessionResponseDto,
} from "../../models/IntegrationsTypes";
import { TYPE, rootContainer } from '../container/root';
import { IApiResponse, IHttpService } from "../http-service";
import {IFetchListArgs, IPagedData} from "../../stores/abstract-paged-data-store";
import { createPagedRequestURLSearchParams } from "../helpers";

export enum WALLET_TRANSACTION_ENUM {
    TYPE_WITHDRAWAL = 'withdrawal',
    TYPE_ADMIN_WITHDRAWAL = 'admin-withdrawal',
    TYPE_MANUAL_DEPOSIT = 'manual-deposit',
    TYPE_AUTO_DEPOSIT = 'auto-deposit',
    TYPE_ADMIN_DEPOSIT = 'admin-deposit',
    TYPE_PROMOTION_DEPOSIT = 'promotion-deposit',
    TYPE_DEBT = 'debt',
    TYPE_REFUND = 'refund',
}

export enum WALLET_TRANSACTION_STATUS_ENUM {
    STATUS_PENDING = 'pending',
    STATUS_SUCCEEDED = 'succeeded',
    STATUS_ADJUSTED = 'adjusted',
    STATUS_CANCELLED = 'cancelled',
    STATUS_FAILED = 'failed',
}

export enum WALLET_PROMOTION_TYPE_ENUM {
    TYPE_FIRST_CHARGE_BONUS = 'first-charge-bonus',
}

export interface IWalletTransaction {
    id: number;
    created_at: string;
    type: WALLET_TRANSACTION_ENUM;
    status: WALLET_TRANSACTION_STATUS_ENUM;
    return_id: number;
    amount: number;
    wallet_after_amount: number | null;
    reason: string | null;
    currency: string;
}

export interface IWalletSetup {
    id: number;
    card_brand: string;
    card_expiration: string;
    card_last4: number;
    payment_method_error: string | null;
}

export interface IWallet {
    id: number;
    shop_id: number;
    amount: number | null;
    recharge_threshold: number | null;
    recharge_amount: number | null;
    auto_recharge: boolean;
    currency: string;
    current_wallet_setup: IWalletSetup | null;
    last_recharge: IWalletTransaction | null;
}

export interface IFetchFailedDepositTransactionsResponseDto {
    failed_transactions: IWalletTransaction[];
    wallet_currency_code: string;
    card_brand: string;
    card_last4: string;
}

export enum PLAN_CYCLE_ENUM {
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
    INSTALLMENTS = 'INSTALLMENTS'
}

export interface IWalletSettingsPlan {
    plan_name: string,
    plan_cycle: PLAN_CYCLE_ENUM,
    real_plan_price: number,
}

export interface IWalletSettings {
    maximum_recharge_amount: number;
    shop_currency: string;
    minimum_deposit_amount: number;
    maximum_deposit_amount: number;
    plan?: IWalletSettingsPlan;
}

export interface IWalletWithSettings {
    settings: IWalletSettings;
    wallet: IWallet;
    eligible_promotions: IWalletEligiblePromotion[];
}

export interface IWalletEligiblePromotion {
    id: number;
    type: WALLET_PROMOTION_TYPE_ENUM;
    title: string
    description: string;
    amount_usd: number;
}

export interface IFetchBillingHistoryArgs extends IFetchListArgs {
}

export interface IWalletWithEligiblePromotions {
    wallet: IWallet;
    eligible_promotions: IWalletEligiblePromotion[];
}

@injectable()
class WalletService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async createCheckoutSessionWithSetupIntent(wallet?: IWallet): Promise<ICreateCheckoutSessionResponseDto> {
        // body is either wallet model, or empty
        // if empty - it will create wallet, otherwise it will use existing one
        const body = wallet?.id ? { id: wallet.id } : {};
        const res = await this.httpService.post<IApiResponse<ICreateCheckoutSessionResponseDto>>(`/rms-api/wallet-setup/create-checkout-session-with-setup-intent`, body);
        return res.payload;
    }

    async updateWallet(wallet: IWallet): Promise<IWallet> {
        const res = await this.httpService.put<IApiResponse<IWallet>>(`/rms-api/wallet`, wallet);
        return res.payload;
    }

    async confirmCheckoutSessionWithSetupIntent(createCheckoutSessionResponse: ICreateCheckoutSessionResponseDto): Promise<IWallet> {
        const res = await this.httpService.post<IApiResponse<IWallet>>(`/rms-api/wallet-setup/confirm-checkout-session-with-setup-intent`, createCheckoutSessionResponse);
        return res.payload;
    }

    async fetchWallet(): Promise<IWalletWithSettings> {
        const res = await this.httpService.get<IApiResponse<IWalletWithSettings>>(`/rms-api/wallet`);
        return res.payload;
    }

    async deletePaymentMethod(walletSetupId: number): Promise<IWallet> {
        const body = {
            id: walletSetupId,
        };
        const res = await this.httpService.post<IApiResponse<IWallet>>(`/rms-api/wallet-setup/delete-payment-method`, body);
        return res.payload;
    }

    async autoDeposit(wallet: IWallet): Promise<IWalletWithEligiblePromotions> {
        const res = await this.httpService.post<IApiResponse<IWalletWithEligiblePromotions>>(`/rms-api/wallet/auto-deposit`, wallet);
        return res.payload;
    }

    async manualDeposit(amount: number): Promise<IWalletWithEligiblePromotions> {
        const res = await this.httpService.post<IApiResponse<IWalletWithEligiblePromotions>>(`/rms-api/wallet/manual-deposit`, { amount });
        return res.payload;
    }

    async fetchBillingHistory(args: IFetchBillingHistoryArgs): Promise<IPagedData<IWalletTransaction>> {
        const {
            page,
            perPage,
            sortBy,
            sortDir,
        } = args;

        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);

        const res = await this.httpService.get<IApiResponse<IPagedData<IWalletTransaction>>>(`/rms-api/wallet/billing-history?${searchParams.toString()}`);

        return res.payload;
    }

    async fetchFailedDepositTransactions(): Promise<IFetchFailedDepositTransactionsResponseDto> {
        const res = await this.httpService.get<IApiResponse<IFetchFailedDepositTransactionsResponseDto>>(`/rms-api/wallet/failed-deposit-transactions`);
        return res.payload;
    }

    async retryLastDeposit(): Promise<IFetchFailedDepositTransactionsResponseDto> {
        const res = await this.httpService.post<IApiResponse<IFetchFailedDepositTransactionsResponseDto>>(`/rms-api/wallet/retry-last-deposit`);
        return res.payload;
    }
}

export default WalletService;
